import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { glass } from '../glassStyles'

// 権限管理メニュー定義: key と、サイトが許可されている前提となる Site の enable_* フラグ
const ALL_MENU_ITEMS = [
  { key: 'reco_media', label: 'Reco-Media (AIレコメンド)', siteFlag: 'enable_reco_media' },
  { key: 'content_analysis', label: 'Media-DRIVER (コンテンツ生成)', siteFlag: 'enable_content_analysis' },
  { key: 'geo_llmo', label: 'GEO/LLMO最適化 (AI検索最適化)', siteFlag: 'enable_geo_llmo' },
  { key: 'review', label: 'UGC Intelligence', siteFlag: 'enable_review' },
  { key: 'ai_purchase_assist', label: 'Findee', siteFlag: 'enable_ai_purchase_assist' },
  { key: 'article_upload', label: 'アップロード管理', siteFlag: 'enable_article_upload' },
]

export default function PermissionManagement() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()
  const [site, setSite] = useState(null)
  const [templates, setTemplates] = useState([])
  const [operators, setOperators] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({ name: '', menu_permissions: {}, can_approve_article: false })
  const [newForm, setNewForm] = useState(null)
  const [saving, setSaving] = useState(false)
  // オペレータへの割り当て変更
  const [assignments, setAssignments] = useState({})

  const load = () => {
    // サイト情報を先に取得して enable_* フラグを得る
    fetch(`/api/sites/${siteId}`, { headers: authHeaders() })
      .then(r => r.json()).then(s => setSite(s)).catch(() => {})
    fetch(`/api/sites/${siteId}/permission-templates`, { headers: authHeaders() })
      .then(r => r.json()).then(data => {
        setTemplates(data)
        fetch(`/api/sites/${siteId}/operators`, { headers: authHeaders() })
          .then(r => r.json()).then(ops => {
            setOperators(ops)
            const a = {}
            ops.forEach(op => { a[op.id] = op.permission_template_id || '' })
            setAssignments(a)
          }).catch(() => {})
      }).catch(() => {})
  }
  useEffect(load, [siteId])

  // サイト (Site) で許可されているメニュー項目のみに絞り込む
  const MENU_ITEMS = site
    ? ALL_MENU_ITEMS.filter((m) => !!site[m.siteFlag])
    : []

  const startEdit = (t) => {
    setEditingId(t.id)
    setEditForm({ name: t.name, menu_permissions: { ...t.menu_permissions }, can_approve_article: t.can_approve_article })
    setNewForm(null)
  }

  const startNew = () => {
    setNewForm({ name: '', menu_permissions: {}, can_approve_article: false })
    setEditingId(null)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (editingId) {
        await fetch(`/api/sites/${siteId}/permission-templates/${editingId}`, {
          method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(editForm),
        })
      }
      setEditingId(null)
      load()
    } catch {}
    finally { setSaving(false) }
  }

  const handleCreate = async () => {
    if (!newForm.name) return
    setSaving(true)
    try {
      await fetch(`/api/sites/${siteId}/permission-templates`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newForm),
      })
      setNewForm(null)
      load()
    } catch {}
    finally { setSaving(false) }
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`テンプレート「${name}」を削除しますか？`)) return
    const res = await fetch(`/api/sites/${siteId}/permission-templates/${id}`, { method: 'DELETE', headers: authHeaders() })
    if (!res.ok) { const err = await res.json(); alert(err.detail || 'エラー'); return }
    load()
  }

  const handleAssignSave = async () => {
    setSaving(true)
    try {
      for (const [opId, tid] of Object.entries(assignments)) {
        const op = operators.find(o => o.id === parseInt(opId))
        if (!op || op.role === 'site_owner') continue
        if ((op.permission_template_id || '') != tid) {
          await fetch(`/api/sites/${siteId}/operators/${opId}`, {
            method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ permission_template_id: tid || null }),
          })
        }
      }
      load()
    } catch {}
    finally { setSaving(false) }
  }

  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 13, boxSizing: 'border-box' }

  // 権限チェックボックスレンダラー
  const PermCheckboxes = ({ perms, setPerms, canApprove, setCanApprove }) => (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>メニュー利用権限</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {MENU_ITEMS.map(m => (
          <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
            <input type="checkbox" checked={!!perms[m.key]} onChange={e => setPerms({ ...perms, [m.key]: e.target.checked })} />
            {m.label}
          </label>
        ))}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>承認権限</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
        <input type="checkbox" checked={canApprove} onChange={e => setCanApprove(e.target.checked)} />
        記事更新承認権限
      </label>
    </div>
  )

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'rgba(255,255,255,0.9)', margin: '0 0 8px' }}>権限管理</h1>
      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: '0 0 20px' }}>
        権限テンプレートを作成し、オペレータに割り当てます
      </p>

      {/* 権限テンプレート一覧 */}
      <div style={{ ...glass.cardDark, padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.9)', margin: 0 }}>権限テンプレート</h3>
          <button onClick={startNew} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.1)', color: '#a5b4fc', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            + 新規作成
          </button>
        </div>

        {/* テンプレートテーブル */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>テンプレート名</th>
                {MENU_ITEMS.map(m => (
                  <th key={m.key} style={{ textAlign: 'center', padding: '8px 6px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>{m.label}</th>
                ))}
                <th style={{ textAlign: 'center', padding: '8px 6px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap' }}>記事承認</th>
                <th style={{ textAlign: 'center', padding: '8px 6px', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '10px', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                    {t.name}{t.is_system ? <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginLeft: 6 }}>システム</span> : ''}
                  </td>
                  {MENU_ITEMS.map(m => (
                    <td key={m.key} style={{ textAlign: 'center', padding: '10px 6px' }}>
                      <span style={{ color: t.menu_permissions[m.key] ? '#10b981' : 'rgba(255,255,255,0.15)', fontSize: 16 }}>
                        {t.menu_permissions[m.key] ? '✓' : '—'}
                      </span>
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', padding: '10px 6px' }}>
                    <span style={{ color: t.can_approve_article ? '#10b981' : 'rgba(255,255,255,0.15)', fontSize: 16 }}>
                      {t.can_approve_article ? '✓' : '—'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '10px 6px' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      <button onClick={() => startEdit(t)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.1)', color: '#a5b4fc', fontSize: 10, cursor: 'pointer' }}>編集</button>
                      {!t.is_system && <button onClick={() => handleDelete(t.id, t.name)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 10, cursor: 'pointer' }}>削除</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 編集フォーム */}
        {editingId && (
          <div style={{ marginTop: 16, padding: 16, borderRadius: 10, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc', marginBottom: 12 }}>テンプレート編集: {editForm.name}</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 4 }}>テンプレート名</label>
              <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={{ ...inputStyle, maxWidth: 300 }} />
            </div>
            <PermCheckboxes perms={editForm.menu_permissions} setPerms={p => setEditForm({ ...editForm, menu_permissions: p })} canApprove={editForm.can_approve_article} setCanApprove={v => setEditForm({ ...editForm, can_approve_article: v })} />
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button onClick={handleSave} disabled={saving} style={{ ...glass.btn, ...glass.btnPrimary, padding: '8px 24px', fontSize: 12, opacity: saving ? 0.6 : 1 }}>{saving ? '保存中...' : '保存'}</button>
              <button onClick={() => setEditingId(null)} style={{ ...glass.btn, padding: '8px 24px', fontSize: 12, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}>キャンセル</button>
            </div>
          </div>
        )}

        {/* 新規作成フォーム */}
        {newForm && (
          <div style={{ marginTop: 16, padding: 16, borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6ee7b7', marginBottom: 12 }}>新規テンプレート作成</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 4 }}>テンプレート名 *</label>
              <input value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })} placeholder="例: コンテンツ担当" style={{ ...inputStyle, maxWidth: 300 }} />
            </div>
            <PermCheckboxes perms={newForm.menu_permissions} setPerms={p => setNewForm({ ...newForm, menu_permissions: p })} canApprove={newForm.can_approve_article} setCanApprove={v => setNewForm({ ...newForm, can_approve_article: v })} />
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button onClick={handleCreate} disabled={saving || !newForm.name} style={{ ...glass.btn, ...glass.btnPrimary, padding: '8px 24px', fontSize: 12, opacity: saving || !newForm.name ? 0.5 : 1 }}>{saving ? '作成中...' : '作成'}</button>
              <button onClick={() => setNewForm(null)} style={{ ...glass.btn, padding: '8px 24px', fontSize: 12, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}>キャンセル</button>
            </div>
          </div>
        )}
      </div>

      {/* オペレータへの割り当て */}
      <div style={{ ...glass.cardDark, padding: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.9)', margin: '0 0 16px' }}>オペレータへの割り当て</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {operators.map(op => (
            <div key={op.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>{op.name}</span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>{op.login_id}</span>
              </div>
              {op.role === 'site_owner' ? (
                <span style={{ padding: '4px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>サイトオーナー（全権限）</span>
              ) : (
                <select
                  value={assignments[op.id] || ''}
                  onChange={e => setAssignments({ ...assignments, [op.id]: e.target.value ? parseInt(e.target.value) : '' })}
                  style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 12 }}
                >
                  <option value="">-- 未設定 --</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </div>
          ))}
          {operators.length === 0 && (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13, padding: '20px 0' }}>オペレータが登録されていません</div>
          )}
        </div>
        {operators.filter(op => op.role !== 'site_owner').length > 0 && (
          <div style={{ marginTop: 16 }}>
            <button onClick={handleAssignSave} disabled={saving}
              style={{ ...glass.btn, ...glass.btnPrimary, padding: '10px 28px', fontSize: 13, opacity: saving ? 0.6 : 1 }}>
              {saving ? '保存中...' : '割り当てを保存'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
